/**
 * Eigenbau: GitLab-Nachzug in der neuen Einstellungs-Hülle.
 *
 * Fachlich unverändert: einmaliger API-Nachzug für verknüpfte Projekte.
 */

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Leerzustand } from "@/components/Leerzustand";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Select";
import { schluessel } from "@/lib/abfragen";
import { api } from "@/lib/api";
import type { Meldung } from "@/lib/meldung";
import {
  EinstellungenHuelle,
  EinstellungsAbschnitt,
  FehlerBlock,
  LadenZeile,
  meldungFuer,
} from "./gemeinsam";

type GitlabNachzugErgebnis = {
  eingerichtet: boolean;
  synchronisiert: boolean;
  mergeRequests: number;
  pipelines: number;
  commits: number;
  auslieferungen: number;
  unzugeordnet: number;
  hinweise: string[];
};

export function EinstellungenGitlab() {
  const projekte = useQuery({
    queryKey: schluessel.gitlabProjekteVerknuepft(),
    queryFn: () => api.gitlab.projekteVerknuepft.query(),
  });
  const [projektId, setzeProjektId] = useState("");
  const [ergebnis, setzeErgebnis] = useState<GitlabNachzugErgebnis | null>(
    null,
  );
  const [fehler, setzeFehler] = useState<Meldung | null>(null);

  const synchronisieren = useMutation({
    mutationFn: (id: string) =>
      api.gitlab.synchronisieren.mutate({ projektId: id }),
    onSuccess: (r) => {
      setzeFehler(null);
      setzeErgebnis(r);
    },
    onError: (e) => {
      setzeErgebnis(null);
      setzeFehler(meldungFuer(e, "Synchronisierung fehlgeschlagen."));
    },
  });

  const gewaehlt =
    (projekte.data ?? []).find((p) => p.id === projektId) ?? null;

  /* Eigene Auswahl fürs Lösen: Sie soll nicht am Nachzug hängen. */
  const [loesenProjektId, setzeLoesenProjektId] = useState("");
  const [bestaetigeLoesen, setzeBestaetigeLoesen] = useState(false);
  const [loesenFehler, setzeLoesenFehler] = useState<Meldung | null>(null);
  const [geloest, setzeGeloest] = useState<string | null>(null);
  const zuLoesen =
    (projekte.data ?? []).find((p) => p.id === loesenProjektId) ?? null;

  const loesen = useMutation({
    mutationFn: (projekt: { id: string }) =>
      api.gitlab.entferneProjekt.mutate({ projektId: projekt.id }),
    onSuccess: (_, projekt) => {
      const name =
        (projekte.data ?? []).find((p) => p.id === projekt.id)?.gitlabProjekt ??
        "Die Verknüpfung";
      setzeLoesenFehler(null);
      setzeGeloest(name);
      setzeLoesenProjektId("");
      void projekte.refetch();
    },
    onError: (e) => {
      setzeGeloest(null);
      setzeLoesenFehler(meldungFuer(e, "Lösen fehlgeschlagen."));
    },
  });

  return (
    <EinstellungenHuelle
      aktiv="gitlab"
      beschreibung="GitLab-Daten eines Projekts einmal über die API nachziehen."
    >
      <EinstellungsAbschnitt
        titel="Nachzug"
        beschreibung="Merge Requests, Pipelines, Commits und Auslieferungen einmal holen. Danach übernimmt der Webhook den laufenden Zufluss. Benötigt „gitlab.konfigurieren“."
      >
        {projekte.isLoading ? (
          <LadenZeile text="Lade Projekte …" />
        ) : projekte.isError ? (
          <FehlerBlock
            fehler={projekte.error}
            fallback="Projekte konnten nicht geladen werden."
            onErneut={() => void projekte.refetch()}
          />
        ) : (projekte.data?.length ?? 0) === 0 ? (
          <Leerzustand
            titel="Kein Projekt mit GitLab-Verknüpfung."
            hinweis="Verknüpfe zuerst ein Projekt im Lieferung-Reiter: Dort trägst du den GitLab-Projektpfad ein."
          />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Select
                value={projektId}
                onValueChange={(wert) => {
                  setzeProjektId(wert);
                  setzeErgebnis(null);
                  setzeFehler(null);
                }}
              >
                <SelectTrigger className="w-80" aria-label="Projekt auswählen">
                  <SelectValue placeholder="Projekt wählen" />
                </SelectTrigger>
                <SelectContent>
                  {projekte.data!.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-[11px] text-tinte4">
                          {p.kuerzel}
                        </span>
                        <span>{p.name}</span>
                        <span className="text-xs text-tinte3">
                          {p.gitlabProjekt}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => {
                  if (gewaehlt) synchronisieren.mutate(gewaehlt.id);
                }}
                disabled={!gewaehlt || synchronisieren.isPending}
                isLoading={synchronisieren.isPending}
                loadingText="Synchronisiere …"
              >
                Synchronisieren
              </Button>
            </div>
            {fehler ? <Fehlerzeile meldung={fehler} /> : null}
            {ergebnis ? (
              <p
                className="text-sm font-medium text-emerald-700 dark:text-emerald-400"
                role="status"
              >
                {ergebnis.eingerichtet
                  ? `Synchronisiert: ${ergebnis.mergeRequests} Merge Requests, ${ergebnis.pipelines} Pipelines, ${ergebnis.commits} Commits, ${ergebnis.auslieferungen} Auslieferungen${ergebnis.unzugeordnet > 0 ? `, ${ergebnis.unzugeordnet} unzugeordnete Merge Requests` : ""}.`
                  : "Die GitLab-API ist nicht eingerichtet — GITLAB_HOST und GITLAB_TOKEN fehlen auf dem Server."}
              </p>
            ) : null}
            {ergebnis?.hinweise && ergebnis.hinweise.length > 0 ? (
              <div
                className="space-y-1 bg-wisch p-3 text-xs"
                role="alert"
              >
                <p className="font-semibold text-tinte">
                  {ergebnis.hinweise.length} Datensätze wurden übersprungen:
                </p>
                {ergebnis.hinweise.map((hinweis, i) => (
                  <p key={i} className="font-mono text-tinte2">
                    {hinweis}
                  </p>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </EinstellungsAbschnitt>

      {/*
        Das Lösen der Verknüpfung stand bis zum 13.08.2026 im Seitenkopf der
        Lieferung — neben der Liste, die man täglich ansieht. Auf Ansage von
        Malwin sitzt es jetzt hier: Es stoppt jeden weiteren Zufluss für das
        ganze Projekt und ist damit eine Einstellung, keine Arbeitshandlung.
        Das Verknüpfen bleibt in der Lieferung, wo der Leerzustand ohnehin
        danach fragt.
      */}
      <EinstellungsAbschnitt
        titel="Verknüpfung lösen"
        beschreibung="Trennt ein Projekt von seinem GitLab-Projekt. Gespeicherte Merge Requests, Pipelines und Commits bleiben erhalten — es kommt nur nichts Neues mehr dazu. Verknüpft wird im Reiter „Lieferung“ des Projekts."
      >
        {(projekte.data?.length ?? 0) === 0 ? (
          <Leerzustand
            titel="Kein Projekt mit GitLab-Verknüpfung."
            hinweis="Es gibt nichts zu lösen."
          />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Select
                value={loesenProjektId}
                onValueChange={(wert) => {
                  setzeLoesenProjektId(wert);
                  setzeLoesenFehler(null);
                  setzeGeloest(null);
                }}
              >
                <SelectTrigger
                  className="w-80"
                  aria-label="Projekt zum Lösen auswählen"
                >
                  <SelectValue placeholder="Projekt wählen" />
                </SelectTrigger>
                <SelectContent>
                  {projekte.data!.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-[11px] text-tinte4">
                          {p.kuerzel}
                        </span>
                        <span>{p.name}</span>
                        <span className="text-xs text-tinte3">
                          {p.gitlabProjekt}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="secondary"
                onClick={() => setzeBestaetigeLoesen(true)}
                disabled={!zuLoesen || loesen.isPending}
                isLoading={loesen.isPending}
                loadingText="Löse …"
              >
                Verknüpfung lösen
              </Button>
            </div>
            {loesenFehler ? <Fehlerzeile meldung={loesenFehler} /> : null}
            {geloest ? (
              <p
                className="text-sm font-medium text-emerald-700 dark:text-emerald-400"
                role="status"
              >
                {geloest} ist gelöst. Neue Merge Requests und Pipelines werden
                diesem Projekt nicht mehr zugeordnet.
              </p>
            ) : null}
          </div>
        )}
      </EinstellungsAbschnitt>

      <Dialog open={bestaetigeLoesen} onOpenChange={setzeBestaetigeLoesen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>GitLab-Verknüpfung lösen?</DialogTitle>
            <DialogDescription>
              {zuLoesen
                ? `„${zuLoesen.gitlabProjekt}“ wird von ${zuLoesen.name} gelöst. Gespeicherte Merge Requests und Pipelines bleiben erhalten, aber neue Zuordnungen stoppen.`
                : "Gespeicherte Merge Requests und Pipelines bleiben erhalten, aber neue Zuordnungen stoppen."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setzeBestaetigeLoesen(false)}
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => {
                setzeBestaetigeLoesen(false);
                if (zuLoesen) loesen.mutate(zuLoesen);
              }}
              disabled={loesen.isPending}
            >
              Lösen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </EinstellungenHuelle>
  );
}
